/**
 * LeaseDocumentSection
 * Displays the current lease document status and available actions
 * Used in LeaseDetailPage for wizard-generated leases (amendments live in LeaseAmendmentsSection)
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  listLeaseDocuments,
  sendLeaseDocumentForSigning,
  voidLeaseDocument,
  leaseDocumentPdfUrl,
  reEditLeaseDocument,
  resendDocumentInvitation,
  getSigningEnvelope,
  ApiClientError,
} from '../../lib/api';
import type { EnvelopeDetailResponse } from '@resida/api-types/lease-support';
import type { LeaseDocumentResponse } from '../../types/lease-generation';
import { formatDateTime } from '../../utils/date';
import { ConfirmDialog } from '../ConfirmDialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

interface LeaseDocumentSectionProps {
  leaseId: string;
  unitId: string;
}

interface PendingInvitationsProps {
  envelopeId: string;
  /** Resends the invitation to one envelope recipient; rejects on failure (e.g. 502 delivery failure). */
  onResend: (recipientId: string) => Promise<unknown>;
  /** Called after a successful resend so the owner can refresh its own state. */
  onResent?: () => void;
}

/**
 * BR-26/BR-27: lists the envelope recipients who have not signed or declined, flags any whose
 * invitation was not delivered, and offers a per-recipient "Resend invitation". Shared by the
 * lease document and amendment sections.
 */
export function PendingInvitations({ envelopeId, onResend, onResent }: PendingInvitationsProps) {
  const [recipients, setRecipients] = useState<EnvelopeDetailResponse['recipients']>([]);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadRecipients = useCallback(async () => {
    try {
      const envelope = (await getSigningEnvelope(envelopeId)) as EnvelopeDetailResponse;
      setRecipients(envelope.recipients ?? []);
    } catch {
      // Non-fatal — the document card still renders its envelope link.
      setRecipients([]);
    }
  }, [envelopeId]);

  useEffect(() => {
    void loadRecipients();
  }, [loadRecipients]);

  async function handleResend(recipient: EnvelopeDetailResponse['recipients'][number]) {
    setResendingId(recipient.id);
    setMessage(null);
    setErrorMessage(null);
    try {
      await onResend(recipient.id);
      setMessage(`Invitation resent to ${recipient.name}`);
      await loadRecipients();
      onResent?.();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  }

  const pending = recipients.filter(
    r => r.recipientType !== 'manager' && r.status !== 'SIGNED' && r.status !== 'DECLINED'
  );
  return (
    <div data-testid="pending-invitations" className="w-full flex flex-col">
      {pending.length > 0 && (
        <ul className="m-0 mb-2 p-0 list-none flex flex-col gap-2" aria-label="Recipients awaiting signature">
          {pending.map(recipient => (
            <li
              key={recipient.id}
              data-testid={`pending-recipient-${recipient.id}`}
              className="flex items-center justify-between gap-3 flex-wrap px-3 py-2 bg-accent/50 rounded"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground m-0">{recipient.name}</p>
                <p className="text-xs text-muted-foreground m-0 break-all">{recipient.email}</p>
                {recipient.invitationState === 'failed' && (
                  <Badge
                    variant="danger"
                    data-testid={`invitation-not-delivered-${recipient.id}`}
                    className="mt-1 w-fit"
                  >
                    Invitation not delivered
                  </Badge>
                )}
              </div>
              <button
                type="button"
                data-testid={`resend-invitation-${recipient.id}`}
                aria-label={`Resend invitation to ${recipient.name}`}
                onClick={() => handleResend(recipient)}
                disabled={resendingId !== null}
                className="px-4 py-2 text-sm font-medium text-primary bg-primary/5 border border-primary/20 rounded-md hover:bg-primary/10 disabled:opacity-50 min-h-[44px]"
              >
                {resendingId === recipient.id ? 'Resending...' : 'Resend invitation'}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* Live regions stay mounted so assistive tech announces text changes. */}
      <p
        role="status"
        data-testid={message ? 'resend-invitation-success' : undefined}
        className="text-sm text-success m-0"
      >
        {message}
      </p>
      <p
        role="alert"
        data-testid={errorMessage ? 'resend-invitation-error' : undefined}
        className="text-sm text-destructive m-0"
      >
        {errorMessage}
      </p>
    </div>
  );
}

export function LeaseDocumentSection({ leaseId, unitId }: LeaseDocumentSectionProps) {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<LeaseDocumentResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [isReediting, setIsReediting] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmText: string;
    isDestructive: boolean;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    isDestructive: false,
    onConfirm: () => {},
  });

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseId]);

  async function loadDocuments(silent = false) {
    try {
      if (!silent) setIsLoading(true);
      setError(null);
      const result = await listLeaseDocuments(leaseId);
      setDocuments(result.documents);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 404) {
        setDocuments([]);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load lease documents');
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleSend(doc: LeaseDocumentResponse) {
    try {
      setIsSending(true);
      setError(null);
      setActionMessage(null);
      await sendLeaseDocumentForSigning(doc.leaseId, doc.id);
      setActionMessage('Sent for signing successfully');
      await loadDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send for signing');
    } finally {
      setIsSending(false);
    }
  }

  async function handleReEdit(doc: LeaseDocumentResponse) {
    setConfirmDialog({
      isOpen: true,
      title: 'Re-Edit Document',
      message: 'This will void the current generated document and create a new draft with your prior edits preserved. Continue?',
      confirmText: 'Re-Edit',
      isDestructive: true,
      onConfirm: async () => {
        try {
          setIsReediting(true);
          setError(null);
          // Single atomic call (BR-28): voids the generated document and returns the new DRAFT,
          // which already holds the prior edits, variables and wizard step 8.
          await reEditLeaseDocument(doc.leaseId, doc.id);
          // Navigate to wizard at Step 8 (Review, Preview & Edit) with the new draft
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          navigate(`/units/${unitId}/create-lease?leaseId=${doc.leaseId}&step=8`);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to start re-edit');
          setIsReediting(false);
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        }
      },
    });
  }

  async function handleVoid(doc: LeaseDocumentResponse) {
    setConfirmDialog({
      isOpen: true,
      title: 'Void Document',
      message: 'Are you sure you want to void this lease document? This cannot be undone.',
      confirmText: 'Void',
      isDestructive: true,
      onConfirm: async () => {
        try {
          setIsVoiding(true);
          setError(null);
          setActionMessage(null);
          await voidLeaseDocument(doc.leaseId, doc.id);
          setActionMessage('Document voided');
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
          await loadDocuments();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to void document');
          setIsVoiding(false);
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        }
      },
    });
  }

  // Get the most recent non-voided document
  const activeDoc = documents.find(d => d.status !== 'VOIDED') ?? documents[0] ?? null;

  function getStatusBadge(status: LeaseDocumentResponse['status']) {
    switch (status) {
      case 'DRAFT':
        return <Badge variant="secondary">Draft</Badge>;
      case 'GENERATED':
        return <Badge variant="primary">Generated</Badge>;
      case 'SENT_FOR_SIGNING':
        return <Badge variant="warning">Awaiting Signatures</Badge>;
      case 'SIGNED':
        return <Badge variant="success">Signed</Badge>;
      case 'VOIDED':
        return <Badge variant="danger">Voided</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  }

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg p-5 border border-border">
        <h2 className="text-lg font-semibold text-foreground mb-4">Lease Document</h2>
        <div
          role="status"
          aria-live="polite"
          aria-label="Loading"
          className="flex items-center gap-2 text-sm text-muted-foreground"
        >
          <div className="w-5 h-5 border-2 border-muted border-t-primary rounded-full animate-spin" aria-hidden="true" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="lease-document-section" className="bg-card rounded-lg p-5 border border-border">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-lg font-semibold text-foreground m-0">Lease Document</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to={`/units/${unitId}/create-lease?leaseId=${leaseId}`}
            className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 no-underline min-h-[44px] inline-flex items-center"
          >
            Create New
          </Link>
        </div>
      </div>

      {error && (
        <div className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-sm text-destructive">
          {error}
        </div>
      )}

      {actionMessage && (
        <div className="p-3 mb-4 bg-success/10 border border-success/20 rounded-md text-sm text-success">
          {actionMessage}
        </div>
      )}

      {!activeDoc ? (
        <div className="py-8 text-center bg-accent/50 rounded-lg border border-dashed border-border">
          <p className="text-sm text-muted-foreground">No lease documents yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Use the wizard to create your first lease document.
          </p>
          <Link
            to={`/units/${unitId}/create-lease`}
            className="mt-3 inline-block px-4 py-2 text-sm font-medium text-primary no-underline hover:underline"
          >
            Start Wizard
          </Link>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Status badge */}
          <div className="flex items-center gap-3">
            <div data-testid="lease-document-status">
              {getStatusBadge(activeDoc.status)}
            </div>
            {activeDoc.generatedAt && (
              <span className="text-xs text-muted-foreground">
                Generated {formatDateTime(activeDoc.generatedAt, { year: 'numeric', month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>

          {/* Jurisdiction chain */}
          {activeDoc.jurisdictionChain.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {activeDoc.jurisdictionChain.map(j => (
                <span key={j} className="px-2 py-0.5 text-xs bg-primary/5 text-primary rounded">
                  {j}
                </span>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-3">
            {activeDoc.status === 'DRAFT' && (
              <Link
                to={`/units/${unitId}/create-lease?draftId=${activeDoc.id}`}
                className="px-4 py-2 text-sm font-medium text-primary bg-primary/5 border border-primary/20 rounded-md hover:bg-primary/10 no-underline min-h-[44px] inline-flex items-center"
              >
                Resume Wizard
              </Link>
            )}

            {activeDoc.status === 'GENERATED' && (
              <>
                <a
                  href={leaseDocumentPdfUrl(leaseId, activeDoc.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="document-view-pdf-link"
                  className="px-4 py-2 text-sm font-medium text-primary bg-primary/5 border border-primary/20 rounded-md hover:bg-primary/10 no-underline min-h-[44px] inline-flex items-center"
                >
                  View PDF
                </a>
                <button
                  type="button"
                  data-testid="document-edit-button"
                  onClick={() => handleReEdit(activeDoc)}
                  disabled={isReediting}
                  className="px-4 py-2 text-sm font-medium text-primary bg-primary/5 border border-primary/20 rounded-md hover:bg-primary/10 disabled:opacity-50 min-h-[44px]"
                >
                  {isReediting ? 'Starting edit...' : 'Edit Document'}
                </button>
                <Button
                  type="button"
                  data-testid="document-send-button"
                  onClick={() => handleSend(activeDoc)}
                  disabled={isSending}
                  className="bg-success text-success-foreground hover:bg-success/90"
                >
                  {isSending ? 'Sending...' : 'Send for Signature'}
                </Button>
                <button
                  type="button"
                  data-testid="document-void-button"
                  onClick={() => handleVoid(activeDoc)}
                  disabled={isVoiding}
                  className="px-4 py-2 text-sm font-medium text-destructive bg-card border border-destructive/20 rounded-md hover:bg-destructive/5 disabled:opacity-50 min-h-[44px]"
                >
                  {isVoiding ? 'Voiding...' : 'Void'}
                </button>
              </>
            )}

            {activeDoc.status === 'SENT_FOR_SIGNING' && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-warning font-medium">Awaiting signatures</span>
                {activeDoc.signingEnvelopeId && (
                  <Link
                    to={`/signing/${activeDoc.signingEnvelopeId}`}
                    data-testid="signing-envelope-link"
                    className="px-4 py-2 text-sm font-medium text-warning bg-warning/10 border border-warning/20 rounded-md hover:bg-warning/10 no-underline min-h-[44px] inline-flex items-center"
                  >
                    View Envelope
                  </Link>
                )}
                {activeDoc.signingEnvelopeId && (
                  <PendingInvitations
                    envelopeId={activeDoc.signingEnvelopeId}
                    onResend={recipientId => resendDocumentInvitation(activeDoc.leaseId, activeDoc.id, recipientId)}
                    onResent={() => void loadDocuments(true)}
                  />
                )}
              </div>
            )}

            {activeDoc.status === 'SIGNED' && (
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-success font-medium">Signing complete</span>
                {activeDoc.signingEnvelopeId && (
                  <Link
                    to={`/signing/${activeDoc.signingEnvelopeId}`}
                    data-testid="signing-envelope-link"
                    className="px-4 py-2 text-sm font-medium text-success bg-success/10 border border-success/20 rounded-md hover:bg-success/10 no-underline min-h-[44px] inline-flex items-center"
                  >
                    View Envelope
                  </Link>
                )}
              </div>
            )}

            {activeDoc.status === 'VOIDED' && (
              <span className="text-sm text-muted-foreground italic">This document has been voided.</span>
            )}
          </div>

          {/* Document history (if more than 1) */}
          {documents.length > 1 && (
            <details className="mt-2">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                View all documents ({documents.length})
              </summary>
              <div className="mt-2 flex flex-col gap-1">
                {documents.map(doc => {
                  let docLink: string | null = null;
                  if (doc.status === 'DRAFT') {
                    docLink = `/landlord/units/${unitId}/create-lease?draftId=${doc.id}`;
                  } else if ((doc.status === 'SENT_FOR_SIGNING' || doc.status === 'SIGNED') && doc.signingEnvelopeId) {
                    docLink = `/landlord/signing/${doc.signingEnvelopeId}`;
                  } else if (doc.status === 'GENERATED') {
                    docLink = `/landlord/api/leases/${leaseId}/lease-documents/${doc.id}/pdf`;
                  }
                  return (
                    <div key={doc.id} className="flex justify-between items-center text-xs text-muted-foreground px-2 py-1 bg-accent/50 rounded">
                      <span>{formatDateTime(doc.createdAt, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(doc.status)}
                        {docLink && (
                          <a
                            href={docLink}
                            target={doc.status === 'GENERATED' ? '_blank' : undefined}
                            rel={doc.status === 'GENERATED' ? 'noopener noreferrer' : undefined}
                            className="text-primary hover:underline"
                          >
                            View
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        isDestructive={confirmDialog.isDestructive}
      />
    </div>
  );
}
